<script setup>
import {computed, inject, onMounted, ref} from "vue";
import useComment, {USE_COMMENT_KEY} from "./CommentComposition";
import CommentThread from "./CommentThread.vue";
import ConfirmModal from "./ConfirmModal.vue";

const siteUrl = document.querySelector('meta[name="site-url"]').content;

//コンポジションを定義、配信
const composition = inject(USE_COMMENT_KEY, useComment());

//0: モーダルを表示しない
//1: 削除確認モーダルを表示する
//2: 削除完了モーダルを表示する
const deleteModalMode = ref(0);

//削除するコメントの ID
const deleteCommentId = ref(0);

//v-for 用のキーを確保するため、コメントスレッドを加工
const threads = computed(() => composition.state.comments.map(thread => {
  //想定されていないが、コメントが一件もないスレッドに対応
  if (
    thread[0] === undefined ||
    thread[0] === null ||
    typeof thread[0] !== 'object' ||
    ! ('id' in thread[0])
  ) {
    return {key: Math.random().toString(36).slice(-16), self: false, thread};
  }

  //そうでなければ一件目のコメント id を key とする
  else {
    return {key: thread[0].id, self: thread.some(comment => Number(comment.is_self) !== 0), thread};
  }
}));

//コメント削除モーダルを表示すると同時に、削除コメント ID を確保
const showDeleteModal = commentId => {
  //削除対象コメント ID をセット
  deleteCommentId.value = commentId;

  //モーダルを開く
  deleteModalMode.value = 1;

  //#modal を確保
  const modal = document.getElementById('modal');

  //#modal を閉じる
  if (modal) {
    modal.classList.add('isDsply');
  }
};

//コメント削除モーダルを非表示
const hideDeleteModal = () => {
  //削除対象の ID をクリアする
  deleteCommentId.value = 0;

  //対象のモーダルを閉じる
  deleteModalMode.value = 0;

  //#modal を確保
  const modal = document.getElementById('modal');

  //#modal を閉じる
  if (modal) {
    modal.classList.remove('isDsply');
  }
};

//コメント削除を実行
const execDelete = async () => {
  //削除を試みる
  const result = await composition.deleteComment(deleteCommentId.value);

  //結果が成功なら削除対象 ID をクリアし、モーダルを表示
  if (result == null) {
    //削除対象 ID をクリア
    deleteCommentId.value = 0;

    //完了モーダルを表示
    deleteModalMode.value = 2;
  }
};

//モーダルを閉じる処理を追加
onMounted(() => {
  //className を持つ祖先を探すが、なければ null を返す
  const _findParent = (className, child) => {
    //自身がクラスを持っていたら返す
    if (child.classList.contains(className)) {
      return child;
    }

    //自身が html 要素なら null　を返す
    if (child instanceof HTMLHtmlElement) {
      return null;
    }

    //そうでなければ親を検索
    return _findParent(className, child.parentElement);
  };

  //モーダルを閉じるアクションを登録したい DOM を得る
  const modalCloseButtons = document.getElementsByClassName('js-modalClose');

  //js-closeModal のイベントでモーダルを非表示にする
  //click イベントを購読し、モーダルを閉じる
  for (let i = 0, max = modalCloseButtons.length; i < max; i = (i + 1) | 0) {
    modalCloseButtons.item(i).addEventListener('click', event => {
      //モーダルの背景ではなく中身をクリックしていたら何もしない
      if ( ! event.currentTarget.classList.contains('js-modalCloseInner') && _findParent('js-modalInner', event.target)) {
        return;
      }

      //モーダルを閉じる
      deleteModalMode.value = 0;
    });
  }
});
</script>

<template>
  <div class="gbMainInner">
    <section
      v-show="composition.state.acceptComments || threads.length > 0"
      id="comment"
    >
      <h2 class="lyMb16 lyPt8 lyPb8 lyPr4 lyPl4 pFont16 pFontBold pBorderBottom2pxSolidBlack">コメント（{{new Intl.NumberFormat().format(composition.state.comments.length)}}）</h2>
      <div v-if="composition.state.comments.length > 0">
        <CommentThread
          v-for="thread in threads"
          :key="thread.key"
          :thread="thread.thread"
          :is-self="thread.self"
          :comment-empathy="composition.state.commentEmpathy"
          :comment-violations="composition.state.commentViolations"
          @delete-comment="showDeleteModal"
        />
      </div>
      <p
        v-else-if="composition.state.acceptComments"
        class="lyPb24 lyPcPd16 pFont14 pFontBold"
      >あたたかいコメントお待ちしております。</p>
    </section>
    <teleport to="body">
      <ConfirmModal
        :is-show="deleteModalMode === 1"
        title-text="コメント削除"
        submit-text="削除する"
        @request-close="hideDeleteModal"
        @request-submit="execDelete"
      >
        <p class="lyMb16 pFont14">投稿したコメントを削除します。<br>よろしいですか？</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="deleteModalMode === 2"
        title-text="コメント削除完了"
        submit-text=""
        @request-close="hideDeleteModal"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">投稿したコメントを削除しました。</p>
      </ConfirmModal>
    </teleport>
  </div>
</template>
